import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { useState, useRef } from "react";
import { usersApi } from "@services/api/users";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { ContactInfos, PartnerInfos, Project } from "types";
import { updateNested, updateNestedArray } from "helpers/updateHelpers";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { MemberAssignmentBlock } from "@components/ui/blocks/MemberAssignmentBlock";
import { CreateContactModal } from "@components/modals/CreateContactModal";
import { AddPartnerToProjectModal } from "@components/modals/AddPartnerToProjectModal";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { invitationsApi } from "@services/api/invitations";
import { getFlutterTime } from "@services/timeHelpers";
import { navigateTo } from "context/navigation.service";
import { default_themes, default_folders } from "@screens/sampleData";
import { projectsApi } from "@services/api";
import DropDown from "@components/ui/dropdown/DropDown";
import { PLANNING_SECTIONS } from "@utils/planning_constants";
import { geocodeAddress } from "@helpers/geocodingHelper";

const createEmptyProject = (authorId: string, orgId: string): Project => {
  const now = getFlutterTime();
  return {
    id: `project_${Date.now()}`,
    infos: {
      general: {
        name: "",
        num: "",
        city: "",
        postal: "",
        address: "",
        creation_date: now,
        last_updated: now,
      },
      client: {
        contacts: [],
      },
      team: {},
      partners: [],
    },
    status: "not_started",
    org_id: orgId,
    author_id: authorId,
    history_events: [],
    themes: structuredClone(default_themes),
    photos: [],
    folders: structuredClone(default_folders),
  };
};

export const CreateProjectModal = ({ onClose }: { onClose: () => void }) => {
  const addProject = useProjectStore((state) => state.addProject);
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const userData = useDataStore((state) => state.userData);

  const firstTemplate = projectTemplates[0];
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(firstTemplate?.id ?? "");
  const [localProject, setLocalProject] = useState<Project>(() => {
    const base = createEmptyProject(userData?.id || "unknown", userData?.org_id || "org1");
    if (firstTemplate) {
      return { ...base, themes: structuredClone(firstTemplate.themes), folders: structuredClone(firstTemplate.folders) };
    }
    return base;
  });
  const [activeTab, setActiveTab] = useState<string>("Général");
  const [showCreateContact, setShowCreateContact] = useState<boolean>(false);
  const [showAddPartner, setShowAddPartner] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  type UserSuggestion = {
    id: string;
    display_name: string;
    email: string;
    photo_url?: string;
  };
  const [contactSuggestions, setContactSuggestions] = useState<
    Record<number, UserSuggestion | null>
  >({});
  const lookupTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {},
  );

  const handleEmailChange = (index: number, value: string) => {
    setLocalProject(
      updateNestedArray(localProject, "infos.client.contacts", index, {
        email: value,
        user_id: undefined,
      }),
    );
    setContactSuggestions((prev) => ({ ...prev, [index]: null }));
    if (lookupTimers.current[index]) clearTimeout(lookupTimers.current[index]);
    if (!value.includes("@") || !value.includes(".")) return;
    lookupTimers.current[index] = setTimeout(async () => {
      const result = await usersApi.lookup(value);
      setContactSuggestions((prev) => ({ ...prev, [index]: result }));
    }, 400);
  };

  const handleLinkContact = (index: number) => {
    const suggestion = contactSuggestions[index];
    if (!suggestion) return;
    const contact = localProject.infos.client.contacts[index];
    const nameParts = suggestion.display_name.split(" ");
    setLocalProject(
      updateNestedArray(localProject, "infos.client.contacts", index, {
        user_id: suggestion.id,
        first_name: contact.first_name || nameParts[0] || "",
        last_name: contact.last_name || nameParts.slice(1).join(" ") || "",
      }),
    );
    setContactSuggestions((prev) => ({ ...prev, [index]: null }));
  };

  async function handleSave() {
    if (!localProject.infos.general.name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du projet est obligatoire",
      });
      return;
    }

    setIsSaving(true);
    try {
      const coordinates = await geocodeAddress(
        localProject.infos.general.address,
        localProject.infos.general.city,
        localProject.infos.general.postal,
      );

      const createdProject = await projectsApi.create({
        name: localProject.infos.general.name,
        num: localProject.infos.general.num,
        org_id: userData?.org_id || "org1",
        status: localProject.status || "not_started",
        address: localProject.infos.general.address,
        city: localProject.infos.general.city,
        postal: localProject.infos.general.postal,
        notes: localProject.infos.general.notes,
        contacts: localProject.infos.client.contacts,
        partners: localProject.infos.partners,
        team: localProject.infos.team,
        themes: localProject.themes,
        folders: localProject.folders,
      });

      if (coordinates) {
        await projectsApi.update(createdProject.id, { coordinates });
        createdProject.infos.general.coordinates = coordinates;
      }

      addProject(createdProject);

      const allContacts = localProject.infos.client.contacts;
      const linkedContacts = allContacts.filter((c) => c.user_id);
      const newContacts = allContacts.filter(
        (c) => !c.user_id && c.email?.trim(),
      );

      // Existing users: add directly to guest_user_ids (no invitation needed)
      if (linkedContacts.length > 0) {
        await projectsApi.update(createdProject.id, {
          guest_user_ids: linkedContacts.map((c) => c.user_id!),
        });
      }

      // New contacts with email: send invitation
      if (newContacts.length > 0) {
        await Promise.allSettled(
          newContacts.map((contact) =>
            invitationsApi.create({
              invitee_email: contact.email!,
              invitee_name:
                `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
                undefined,
              invitation_type: "web",
              context: {
                access_type: "GUEST",
                project_id: createdProject.id,
              },
            }),
          ),
        );
      }

      showNotification({
        type: "success",
        message: "Projet créé avec succès",
      });
      onClose();
      navigateTo({ route: `/project/${createdProject.id}` });
    } catch (error) {
      console.error("Error creating project:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la création du projet",
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <BaseModal
      title="Créer un projet"
      startIcon={<Icon1 name="cube" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <ChunkyTabs
          short_tabs={["Général", "Contacts", "Equipe", "Artisans"]}
          setActiveTab={(tab) => setActiveTab(tab)}
          activeTab={activeTab}
        ></ChunkyTabs>
        <div className="min-h-[200px] max-h-[450px] overflow-y-auto flex flex-col p-1 gap-2">
          {activeTab === "Général" && (
            <>
              <DropDown
                label="Modèle de projet"
                category="Modèle"
                options={[
                  { value: "", label: "Aucun (projet vide)" },
                  ...projectTemplates.map((t) => ({
                    value: t.id,
                    label: t.infos.general.name,
                  })),
                ]}
                value={selectedTemplateId}
                onSelect={(val) => {
                  const id = val as string;
                  setSelectedTemplateId(id);
                  const template = projectTemplates.find((t) => t.id === id);
                  if (template) {
                    setLocalProject((prev) => ({
                      ...prev,
                      themes: structuredClone(template.themes),
                      folders: structuredClone(template.folders),
                    }));
                  } else {
                    setLocalProject((prev) => ({
                      ...prev,
                      themes: structuredClone(default_themes),
                      folders: structuredClone(default_folders),
                    }));
                  }
                }}
              />
              <div className="flex flex-row gap-2">
                <TextField
                  label="Nom"
                  placeholder="DUPONT"
                  mandatory={true}
                  value={localProject.infos.general.name}
                  onChange={(value) =>
                    setLocalProject(
                      updateNested(localProject, "infos.general.name", value),
                    )
                  }
                />
                <TextField
                  label="Numéro"
                  placeholder="A001"
                  value={localProject.infos.general.num}
                  onChange={(value) =>
                    setLocalProject(
                      updateNested(localProject, "infos.general.num", value),
                    )
                  }
                />
              </div>
              <div className="flex flex-row gap-2">
                <TextField
                  label="Ville"
                  placeholder="Rennes"
                  value={localProject.infos.general.city || ""}
                  onChange={(value) =>
                    setLocalProject(
                      updateNested(localProject, "infos.general.city", value),
                    )
                  }
                />
                <TextField
                  label="Postal"
                  placeholder="35000"
                  value={localProject.infos.general.postal || ""}
                  onChange={(value) =>
                    setLocalProject(
                      updateNested(localProject, "infos.general.postal", value),
                    )
                  }
                />
              </div>
              <TextField
                label="Adresse"
                placeholder="1 rue des fleurs"
                value={localProject.infos.general.address || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.address", value),
                  )
                }
              />
              <AreaTextField
                label="Notes"
                placeholder="Notes sur le projet"
                value={localProject.infos.general.notes || ""}
                onChange={(value) =>
                  setLocalProject(
                    updateNested(localProject, "infos.general.notes", value),
                  )
                }
              />
            </>
          )}
          {activeTab === "Contacts" && (
            <>
              <div className="flex flex-col">
                {localProject.infos.client.contacts.map(
                  (contact: ContactInfos, index: number) => (
                    <div
                      key={index}
                      className="flex flex-col gap-2 mb-4 p-4 rounded-xl border"
                    >
                      {/* Email first — triggers lookup */}
                      <div className="flex flex-col gap-1">
                        <TextField
                          label={`Contact ${index + 1} - Email`}
                          placeholder="marie.curie@life.com"
                          value={contact.email || ""}
                          onChange={(value) => handleEmailChange(index, value)}
                        />
                        {contact.user_id && (
                          <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-1">
                            <span>✓</span>
                            <span>Compte existant lié</span>
                          </div>
                        )}
                        {contactSuggestions[index] && !contact.user_id && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                            <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-700 shrink-0">
                              {contactSuggestions[index]!.display_name.charAt(
                                0,
                              ).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-neutral-800 truncate">
                                {contactSuggestions[index]!.display_name}
                              </div>
                              <div className="text-xs text-neutral-400">
                                Compte existant
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleLinkContact(index)}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
                            >
                              Lier ce compte
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setContactSuggestions((prev) => ({
                                  ...prev,
                                  [index]: null,
                                }))
                              }
                              className="text-neutral-400 hover:text-neutral-600 text-sm leading-none"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-row gap-2">
                        <TextField
                          label={`Contact ${index + 1} - Prénom`}
                          placeholder="Jean"
                          value={contact.first_name || ""}
                          onChange={(value) =>
                            setLocalProject(
                              updateNestedArray(
                                localProject,
                                "infos.client.contacts",
                                index,
                                { first_name: value },
                              ),
                            )
                          }
                        />
                        <TextField
                          label={`Contact ${index + 1} - Nom`}
                          placeholder="Dupont"
                          value={contact.last_name || ""}
                          onChange={(value) =>
                            setLocalProject(
                              updateNestedArray(
                                localProject,
                                "infos.client.contacts",
                                index,
                                { last_name: value },
                              ),
                            )
                          }
                        />
                      </div>
                      <TextField
                        label={`Contact ${index + 1} - Téléphone`}
                        placeholder="06 01 02 03 04"
                        type="phone"
                        value={contact.phone_number || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { phone_number: value },
                            ),
                          )
                        }
                      />
                    </div>
                  ),
                )}
              </div>
              <SecondaryButton
                startIcon={<Icon1 name="plus" size={18}></Icon1>}
                label="Nouveau contact"
                variant="large"
                onClick={() => setShowCreateContact(true)}
              />
              {showCreateContact && (
                <CreateContactModal
                  onClose={() => setShowCreateContact(false)}
                  onSave={(newContact) => {
                    setLocalProject({
                      ...localProject,
                      infos: {
                        ...localProject.infos,
                        client: {
                          ...localProject.infos.client,
                          contacts: [
                            ...localProject.infos.client.contacts,
                            newContact,
                          ],
                        },
                      },
                    });
                  }}
                />
              )}
            </>
          )}
          {activeTab === "Equipe" && (
            <>
              <MemberAssignmentBlock
                team={localProject.infos.team}
                onTeamChange={(team) =>
                  setLocalProject({
                    ...localProject,
                    infos: { ...localProject.infos, team },
                  })
                }
              />
            </>
          )}
          {activeTab === "Artisans" && (
            <>
              {showAddPartner && (
                <AddPartnerToProjectModal
                  onClose={() => setShowAddPartner(false)}
                  onSelect={(partner: PartnerInfos) => {
                    if (
                      localProject.infos.partners.some(
                        (p) => p.id === partner.id,
                      )
                    )
                      return;
                    setLocalProject({
                      ...localProject,
                      infos: {
                        ...localProject.infos,
                        partners: [...localProject.infos.partners, partner],
                      },
                    });
                    setShowAddPartner(false);
                  }}
                  existingPartnerIds={localProject.infos.partners.map(
                    (p) => p.id,
                  )}
                />
              )}
              <div className="flex flex-col gap-1">
                {PLANNING_SECTIONS.map((section) => {
                  const candidates = localProject.infos.partners
                    .filter((p) => p.company_types?.includes(section.key))
                    .sort((a, b) => (a.company_types?.length || 0) - (b.company_types?.length || 0));
                  const assigned = candidates[0] || undefined;
                  return (
                    <div
                      key={section.key}
                      className={`flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
                        assigned
                          ? "bg-white border-neutral-200"
                          : "bg-neutral-50 border-neutral-200 cursor-pointer hover:bg-neutral-100"
                      }`}
                      onClick={
                        !assigned ? () => setShowAddPartner(true) : undefined
                      }
                    >
                      <div className="flex flex-row items-center gap-2 flex-1">
                        <div
                          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${assigned ? section.cellColor : "bg-neutral-300"}`}
                        />
                        <div className="flex flex-col">
                          <div
                            className={`text-h3 font-medium ${assigned ? "text-black" : "text-neutral-400"}`}
                          >
                            {section.label}
                          </div>
                          {assigned && (
                            <div className="text-h4 text-neutral-400">
                              {assigned.company_name || assigned.display_name}
                            </div>
                          )}
                        </div>
                      </div>
                      {assigned ? (
                        <SecondaryButton
                          danger={true}
                          variant="small"
                          onClick={() => {
                            setLocalProject({
                              ...localProject,
                              infos: {
                                ...localProject.infos,
                                partners: localProject.infos.partners.filter(
                                  (p) => p.id !== assigned.id,
                                ),
                              },
                            });
                          }}
                        >
                          Retirer
                        </SecondaryButton>
                      ) : (
                        <Icon1 name="plus" size={14} color="grey" />
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Partners whose type is not a planning section specialty */}
              {localProject.infos.partners
                .filter(
                  (p) =>
                    !PLANNING_SECTIONS.some((s) => p.company_types?.includes(s.key)),
                )
                .map((partner) => (
                  <div
                    key={partner.id}
                    className="flex flex-row items-center justify-between px-4 py-3 rounded-lg border"
                  >
                    <div className="flex flex-col">
                      <div className="text-h2 text-black font-medium">
                        {partner.company_name || partner.display_name}
                      </div>
                      <div className="text-h3 text-neutral-400">
                        {partner.role}
                        {partner.company_name && partner.display_name
                          ? ` - ${partner.display_name}`
                          : ""}
                      </div>
                    </div>
                    <SecondaryButton
                      danger={true}
                      variant="small"
                      onClick={() => {
                        setLocalProject({
                          ...localProject,
                          infos: {
                            ...localProject.infos,
                            partners: localProject.infos.partners.filter(
                              (p) => p.id !== partner.id,
                            ),
                          },
                        });
                      }}
                    >
                      Retirer
                    </SecondaryButton>
                  </div>
                ))}
              <SecondaryButton
                startIcon={<Icon1 name="plus" size={16} />}
                label="Ajouter un intervenant"
                variant="large"
                onClick={() => setShowAddPartner(true)}
              />
            </>
          )}
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label="Créer le projet"
          onClick={handleSave}
          loading={isSaving}
        ></Button>
      </div>
    </BaseModal>
  );
};
